import type { CustomBranding } from "../../Models/firebaseHelper.types";

export interface EducationProviderProps {
  children: React.ReactNode;
  onCertificateGenerated?: () => void;
}

export type LevelData = {
  id: string;
  selectedLevelType: LevelType;
  data: unknown | null;
  ids: string | string[] | null;
};

export type Course = {
  ID: string;
  WorkingTitle: string;
  Title: string;
  PublishedStatus: string;
};

export const TheoryTypes: Record<string, LevelType> = {
  THEORY: "theory",
  NORMAL: "normal",
  LOOPS: "loops",
  QUIZ: "quizOnWrongAnswers",
  DIAGNOSTIC_TEST: "diagnosticTest",
  WORDLISTS: "wordlists",
  CHAT: "AIChat",
  ROLEPLAY: "AIRolePlay",
  BRANCHINGSTORY: "branchingStory",
  VIDEO: "video",
};

export type GenericLevelId = string | string[] | null;

export type { CustomBranding };

export type CourseData = {
  ID: string;
  highestLevelCompleted: number;
  span?: string;
  excludedTheoryIds?: string[];
  NumberOfLevels?: number;
  adjustedNumberOfLevels?: number;
};

export type LevelComponentProps = {
  selectedLevelType: LevelType | null;
  levelData: unknown | null;
  levelInfo: Level | null;
  levelCompleted: (showPopup: boolean) => void;
  closePopupWrapper: () => void;
  quizSuccess: (totalCorrectAnswers: number, totalAnswers: number) => void;
  quizFailed: (totalCorrectAnswers: number, totalAnswers: number) => void;
  isLoading: boolean;
  loadError: string | null;
  retryLoad: () => void;
  ids: GenericLevelId;
  isFirstAIChatLevel: boolean;
  currentWordlistIndex: number;
  setCurrentWordlistIndex: (index: number) => void;
  currentQuestionIndex: number;
  setCurrentQuestionIndex: (index: number) => void;
};

export type ReloadCourseDataProps = {
  courseToLoad: Course;
  isSilent?: boolean;
};

export type EducationContextType = {
  courseCompleted: boolean;
  courseData: CourseData | null;
  diagnosticMessage: string | undefined;
  totalLevels: number;
  sortedLevels: Level[];
  levelInfo: Level | null;
  lockedPopupMessage: string | null | undefined;
  highestLevelCompleted: number;
  showQuizSuccessPopup: boolean;
  showQuizFailedPopup: boolean;
  totalCorrectAnswers: number | null;
  totalAnswers: number | null;
  loading: boolean;
  courseDataError: boolean;
  fadeIn: boolean;
  levelComponentProps: LevelComponentProps;
  cacheLevel: (level: Level) => Promise<void>;
  setTotalLevels: (totalLevels: number) => void;
  setSortedLevels: (sortedLevels: Level[]) => void;
  setCourseCompleted: (courseCompleted: boolean) => void;
  setFadeIn: (fadeIn: boolean) => void;
  reloadCourseData: (props: ReloadCourseDataProps) => Promise<void>;
  handleLevelHover: (level: Level) => Promise<void>;
  handleLevelClickWrapper: (course: Course, level: Level) => Promise<void>;
  closeLockedPopup: () => void;
  handleDidCompleteLevelWrapper: (
    course: Course,
    level: Level,
    questionAnswerStates: QuestionAnswerStates
  ) => Promise<void>;
  closeSuccessFailedPopups: () => void;
  handleCloseSuccessPopup: () => void;
  setDiagnosticMessage: (message: string | undefined) => void;
  tempQuestionAnswerStates: QuestionAnswerStates;
  setTempQuestionAnswerStates: React.Dispatch<
    React.SetStateAction<QuestionAnswerStates>
  >;
  updateBackendOnProgress: (
    course: Course,
    level: Level,
    questionAnswerStates: QuestionAnswerStates
  ) => Promise<boolean>;
  onCertificateGenerated?: () => void;
  clickedLevelRect: DOMRect | null;
  setClickedLevelRect: (rect: DOMRect | null) => void;
  triggerLevelCompletionSequence: (level: Level, isLastLevel: boolean) => void;
  levelCompletionAnimation: {
    completedLevelIndex: number;
    signalId: number;
    advanceToNextLevel: boolean;
  } | null;
  triggerCourseCompletedConfetti: boolean;
  setTriggerCourseCompletedConfetti: (trigger: boolean) => void;
  /** True from the final level's completion until the snake unlocks the certificate. */
  isRevealingCourseCompletion: boolean;
  revealCourseCompletion: () => void;
  getPendingCompletedLevel: () => {
    level: Level;
    isLastLevel: boolean;
  } | null;
  clearPendingCompletedLevel: () => void;
};

export type LevelTypeResult = {
  aiChatId: string | null;
  theoryId: string | null;
  questionIds: string[] | null;
  questionLoopIds: string[] | null;
  quizQuestionIds: string[] | null;
  diagnosticQuestionIds: string[] | null;
  diagnosticQuestions: { [key: string]: string[] } | null;
  wordlistIds: string[] | null;
};

export type LevelTypeParams = {
  aiChatId?: string | null;
  theoryId?: string | null;
  questionIds?: string[] | null;
  questionLoopIds?: string[] | null;
  quizQuestionIds?: string[] | null;
  diagnosticQuestionIds?: string[] | null;
  diagnosticQuestions: { [key: string]: string[] } | null;
  wordlistIds?: string[] | null;
  levelInfo?: Level | null;
};

export type QuestionAnswerStates = Array<{
  questionId: string;
  isCorrect: boolean;
}>;

export type LevelType =
  | "theory"
  | "questionsLoop"
  | "questionsCaseStudy"
  | "questionsLinearAnswerMode"
  | "quizOnWrongAnswers"
  | "diagnosticTest"
  | "wordlist"
  | "AIChat"
  | "AIRolePlay"
  | "branchingStory"
  | "video"
  // Duplicates used for UI mapping
  | "normal"
  | "loops"
  | "wordlists";

export type Level = {
  ID: string;
  AppID: string;
  Title: string;
  Name: string;
  LevelType: LevelType;
  ChapterIndex: number;
  LevelIndex: number;
  TotalLevelIndex: number;
  TheoryID: string;
  RolePlayID?: string;
  BranchingStoryID?: string;
  VideoID?: string;
  VideoURL?: string;
  VideoBody?: string;
  /** Optional image URL shown before playback (e.g. from Firestore `PosterURL` on video docs). */
  VideoPosterURL?: string;
  QuestionLoopIds: string[];
  QuestionIDs: string[];
  WordListIDs: string[];
  DiagnosticQuestions?: { [key: string]: string[] };
  IsMandatory?: boolean;
  QuestionAnsweringMode:
    | "linear"
    | "loop"
    | "needToAnswerAllCorrect"
    | "caseStudy"
    | "quizOnWrongAnswers"
    | "diagnosticTest"
    | undefined;
  /** Available question IDs for AI Chat levels that can switch to questions mode */
  AvailableQuestionIDs?: string[];
};
